import { useState } from 'react'

import { MANAGEMENT_QUERY_KEY_GET_PRODUCTS, useManagementGetProducts } from '@apps-common/apis/management/product'
import { ProductModal } from '@apps-common/apps/portal/pages/management/components/product'
import { ImageComponent } from '@apps-common/components/common'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { Table } from '@apps-common/components/elements/table'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { formatCurrency } from '@apps-common/helpers/currency'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IProductListModel } from '@apps-common/types/management/product'

interface IProps {
  searchVal: string
}

export const ProductTableView: React.FC<IProps> = ({ searchVal }) => {
  const [skip, setSkip] = useState<number>(0)
  const [take, setTake] = useState<number>(10)
  const { isLoading, isError, data, refetch } = useManagementGetProducts({
    name: searchVal
  })

  const keys = [
    {
      title: '',
      key: 'productImageUrl',
      static: true,
      thClasses: '',
      tdClasses: '',
      Component: (props: IProductListModel) => (
        <ImageComponent
          src={props.imageUrl}
          alt='Product image'
          containerClasses='bg-surface-2 border border-border w-[36px] h-[36px] rounded-[15px] flex items-center justify-center'
          imageClasses='w-[28px] h-[28px]'
        />
      )
    },
    {
      title: 'Product',
      key: 'productName',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Type',
      key: 'productType',
      thClasses: '',
      tdClasses: ''
    },
    {
      title: 'Price',
      key: 'price',
      thClasses: '',
      tdClasses: '',
      Component: (props: IProductListModel) => <span>{formatCurrency(props.price)}</span>
    },
    {
      title: 'Default',
      key: 'defaultItem',
      thClasses: '',
      tdClasses: '',
      Component: (props: any) => (
        <span>
          {props.defaultItem ? (
            <commonIcons.checkCircle className='text-[1.5em] bg-success rounded-full text-on-success p-[2px]' />
          ) : (
            <commonIcons.close className='text-[1.5em] bg-surface-2 rounded-full text-text p-[2px]' />
          )}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      static: true,
      thClasses: 'text-right',
      tdClasses: '',
      Component: (props: IProductListModel) => (
        <div className='flex items-center justify-end gap-[5px]'>
          <ModalButton
            component={ProductModal}
            componentProps={{
              productId: props.productId
            }}
            confirmCallback={() => {
              queryClient.refetchQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_PRODUCTS(searchVal) })
            }}
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
          >
            <ButtonRoundSm type='div' className='action-brand-solid'>
              <commonIcons.arrowAngled />
            </ButtonRoundSm>
          </ModalButton>
        </div>
      )
    }
  ]

  const loading = isLoading || isError || !data

  return (
    <LayoutContainer>
      <LayoutPadding topPadding='pt-[65px] sm:pt-[25px]'>
        <Table
          loading={loading}
          loadingSkeletonHeight='leading-[36px]'
          placeholderSkeletonHeight='leading-[36px]'
          pagination={{
            loading,
            count: data?.length || 0,
            skip,
            take,
            onSkipChanged: (value: number) => setSkip(value),
            onTakeChanged: (value: number) => setTake(value)
          }}
          keys={keys}
          data={data?.map((d) => d) || []}
          sliceData
          topLeftComponent={
            <>
              {!loading && data && (
                <div className='jost-light'>
                  <span className='jost-medium'>{data?.length}</span> product results
                </div>
              )}
              {(loading || !data) && <div className='h-[22px] w-[100px] bg-skeleton rounded-lg' />}
            </>
          }
          onSkipChanged={(value: number) => setSkip(value)}
          onTakeChanged={(value: number) => setTake(value)}
        />
      </LayoutPadding>
    </LayoutContainer>
  )
}
